import * as React from "react";
import { Alert, Button, Card, Divider, Dropdown, notification } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { useDrawerContext } from "../../../components/antd/drawerContext";
import { getMetricOptions } from "./utils/utils";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../envVariables";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { KedaTrigger, WorkloadAutoscalingKeda } from "../../../schema/types/workload/workloadOptions";
import { allKedaScalers, kedaScalerOptions } from "./kedaUtils";
import { DrawerWorkloadOptions } from "../../../components/drawer/options";
import { formSet, formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpDescription } from "../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpInputNumber } from "../../gvc/testNewForm/CpInputNumber";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import jsYaml from "js-yaml";
import { CpErrorWarningArray } from "../../gvc/testNewForm/CpErrorWarningArray";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "./only";
import { store } from "../../../store";

interface Props {}
export const WorkloadDefaultOptions: React.FC<Props> = React.memo(({}) => {
  const { id } = useCpFormContext();
  const drawer = useDrawerContext();

  const type = useCpFormValue(`type`);
  const gvc = useCpFormValue(`gvc`);

  const alreadySet = useCpFormValue(`defaultOptions.alreadySet`);
  const override = useCpFormValue(`defaultOptions.override`);
  const capacityAI = useCpFormValue(`defaultOptions.capacityAI`);
  const metric = useCpFormValue(`defaultOptions.autoscaling.metric`);
  const keda = useCpFormValue(`defaultOptions.autoscaling.keda`);
  const minScale = useCpFormValue(`defaultOptions.autoscaling.minScale`);

  const [scaleToZeroSuggestionApplied, setScaleToZeroSuggestionApplied] = React.useState(false);

  React.useEffect(() => {
    if (minScale !== "0") {
      setScaleToZeroSuggestionApplied(false);
    }
  }, [minScale]);

  const applyScaleToZeroSuggestion = React.useCallback(() => {
    formSetValue(id, `defaultOptions.autoscaling.scaleToZeroDelay`, "30");
    formSetValue(id, `defaultOptions.timeoutSeconds`, "120");
    formSetValue(id, `defaultOptions.autoscaling.metric`, "concurrency");
    setScaleToZeroSuggestionApplied(true);
  }, [id]);

  const metricOptions = React.useMemo(() => {
    return getMetricOptions(type);
  }, [type]);

  return (
    <div className="cpln-form-section cpln-ant-tab-content">
      <Card>
        <CardTitle>Default Options</CardTitle>
        <CardContent>
          {alreadySet ? null : (
            <>
              <CpFormItem noMargin>
                <CpLabelTooltip>
                  <CpLabel name={`defaultOptions.override`}>
                    Override Default Options
                    <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                      <CpLabelDirtyIndicator name={`defaultOptions.override`} />
                    </Only>
                  </CpLabel>
                  <CpSwitch name={`defaultOptions.override`} disabled={metric === "cpu"} />
                  <CpTooltip tooltipProps={{ title: `When not overridden, it will use the default settings` }} />
                </CpLabelTooltip>
              </CpFormItem>
            </>
          )}
          {(alreadySet || override) && type == "cron" ? (
            <>
              <CpFormItem noMargin={alreadySet}>
                <div
                  className="text-sm link cursor-pointer"
                  onClick={() =>
                    drawer.setTemporary(
                      DrawerWorkloadOptions.key,
                      DrawerWorkloadOptions.title,
                      DrawerWorkloadOptions.Content(type),
                    )
                  }
                >
                  Details
                </div>
                <div className="flex items-center gap-16">
                  {type === "stateful" ? null : (
                    <div className="flex items-center gap-2">
                      <CpLabel name={`defaultOptions.capacityAI`}>
                        Capacity AI
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`defaultOptions.capacityAI`} />
                        </Only>
                      </CpLabel>
                      <CpSwitch name={`defaultOptions.capacityAI`} disabled={metric === "cpu"} />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <CpLabel name={`defaultOptions.suspend`}>
                      Suspend
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.suspend`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`defaultOptions.suspend`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`defaultOptions.multiZone.isEnabled`}>
                      Multi Zone
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.multiZone.isEnabled`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`defaultOptions.multiZone.isEnabled`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[`defaultOptions.capacityAI`, `defaultOptions.suspend`, `defaultOptions.multiZone.isEnabled`]}
                />
              </CpFormItem>
              {capacityAI ? (
                <CpFormItem>
                  <CpLabel name={`defaultOptions.capacityAIUpdateMinutes`}>
                    Capacity AI Update Minutes
                    <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                      <CpLabelDirtyIndicator name={`defaultOptions.capacityAIUpdateMinutes`} />
                    </Only>
                  </CpLabel>
                  <CpDescription>How often reservations are adjusted</CpDescription>
                  <CpInputNumber name={`defaultOptions.capacityAIUpdateMinutes`} style={{ width: 140 }} />
                  <CpErrorWarning name={`defaultOptions.capacityAIUpdateMinutes`} />
                </CpFormItem>
              ) : null}
            </>
          ) : null}
          {(alreadySet || override) && type !== "cron" ? (
            <>
              <CpFormItem noMargin={alreadySet}>
                {metric === "cpu" ? (
                  <CpDescription>
                    Capacity AI is not available when Scaling Strategy is set to CPU Utilization
                  </CpDescription>
                ) : null}
                <div
                  className="text-sm link cursor-pointer"
                  onClick={() =>
                    drawer.setTemporary(
                      DrawerWorkloadOptions.key,
                      DrawerWorkloadOptions.title,
                      DrawerWorkloadOptions.Content(type),
                    )
                  }
                >
                  Details
                </div>
                <div className="flex items-center gap-16">
                  <div className="flex items-center gap-2">
                    <CpLabel name={`defaultOptions.capacityAI`}>
                      Capacity AI
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.capacityAI`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`defaultOptions.capacityAI`} disabled={metric === "cpu"} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`defaultOptions.debug`}>
                      Debug
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.debug`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`defaultOptions.debug`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`defaultOptions.suspend`}>
                      Suspend
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.suspend`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`defaultOptions.suspend`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`defaultOptions.multiZone.isEnabled`}>
                      Multi Zone
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.multiZone.isEnabled`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`defaultOptions.multiZone.isEnabled`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[
                    `defaultOptions.capacityAI`,
                    `defaultOptions.debug`,
                    `defaultOptions.suspend`,
                    `defaultOptions.multiZone.isEnabled`,
                  ]}
                />
              </CpFormItem>
              {capacityAI ? (
                <CpFormItem>
                  <CpLabelTooltip>
                    <CpLabel name={`defaultOptions.capacityAIUpdateMinutes`}>
                      Capacity AI Update Minutes
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.capacityAIUpdateMinutes`} />
                      </Only>
                    </CpLabel>
                    <CpTooltip tooltipProps={{ title: `How often reservations are adjusted` }} />
                  </CpLabelTooltip>
                  <CpInputNumber name={`defaultOptions.capacityAIUpdateMinutes`} style={{ width: 140 }} />
                  <CpErrorWarning name={`defaultOptions.capacityAIUpdateMinutes`} />
                </CpFormItem>
              ) : null}
              {/* TODO later we can use inputnumber here for all things with validations of number */}
              {/* TODO This looks like do not check validation again when workload type is changed, same in volumeset initial capacity */}
              {/* TODO can show min max values in inputs */}
              <CpFormItem>
                <CpLabel name={`defaultOptions.timeoutSeconds`}>
                  Timeout Seconds
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`defaultOptions.timeoutSeconds`} />
                  </Only>
                </CpLabel>
                <CpInputNumber name={`defaultOptions.timeoutSeconds`} style={{ width: 140 }} />
                <CpErrorWarning name={`defaultOptions.timeoutSeconds`} />
              </CpFormItem>
              <Divider orientation={`left`} orientationMargin={0}>
                Auto Scaling
              </Divider>
              {metric === "keda" ? (
                <div>
                  {metric === "keda" && !gvc.item?.spec.keda?.enabled ? (
                    <Alert
                      showIcon
                      className="my-2 w-fit"
                      type={`warning`}
                      message={
                        <div>
                          <div>
                            KEDA autoscaling is configured for this workload but will not function because KEDA is not
                            enabled in its GVC.
                          </div>
                          <div>Please enable KEDA in the GVC settings to activate KEDA autoscaling.</div>
                        </div>
                      }
                    />
                  ) : null}
                </div>
              ) : null}
              <CpFormItem>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col">
                    <CpLabel name={`defaultOptions.autoscaling.metric`}>
                      Metric
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.metric`} />
                      </Only>
                    </CpLabel>
                    <CpSelect
                      name={`defaultOptions.autoscaling.metric`}
                      options={metricOptions}
                      style={{ minWidth: 300 }}
                    />
                  </div>
                  {["disabled", "multi", "keda"].includes(metric) ? null : (
                    <div className="flex flex-col">
                      <CpLabel name={`defaultOptions.autoscaling.target`}>
                        Target
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.target`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`defaultOptions.autoscaling.target`} />
                    </div>
                  )}
                  {metric === "latency" ? (
                    <div className="flex flex-col">
                      <CpLabel name={`defaultOptions.autoscaling.metricPercentile`}>
                        Metric Percentile
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.metricPercentile`} />
                        </Only>
                      </CpLabel>
                      <CpSelect
                        name={`defaultOptions.autoscaling.metricPercentile`}
                        options={[
                          { label: "p50", value: "p50" },
                          { label: "p75", value: "p75" },
                          { label: "p99", value: "p99" },
                        ]}
                      />
                    </div>
                  ) : null}
                  {metric === "multi" ? (
                    <>
                      <div className="flex flex-col">
                        <CpLabel name={`defaultOptions.autoscaling.multi.cpu`}>
                          CPU Target
                          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                            <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.multi.cpu`} />
                          </Only>
                        </CpLabel>
                        <CpInputNumber name={`defaultOptions.autoscaling.multi.cpu`} />
                      </div>
                      <div className="flex flex-col">
                        <CpLabel name={`defaultOptions.autoscaling.multi.memory`}>
                          Memory Target
                          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                            <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.multi.memory`} />
                          </Only>
                        </CpLabel>
                        <CpInputNumber name={`defaultOptions.autoscaling.multi.memory`} />
                      </div>
                      <div className="flex flex-col">
                        <CpLabel name={`defaultOptions.autoscaling.multi.rps`}>
                          RPS Target
                          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                            <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.multi.rps`} />
                          </Only>
                        </CpLabel>
                        <CpInputNumber name={`defaultOptions.autoscaling.multi.rps`} />
                      </div>
                    </>
                  ) : null}
                </div>
                <CpErrorWarningArray
                  names={[
                    `defaultOptions.autoscaling.metric`,
                    `defaultOptions.autoscaling.target`,
                    `defaultOptions.autoscaling.metricPercentile`,
                    `defaultOptions.autoscaling.multi.cpu`,
                    `defaultOptions.autoscaling.multi.memory`,
                    `defaultOptions.autoscaling.multi.rps`,
                  ]}
                />
              </CpFormItem>
              {metric === "keda" ? (
                <div className="mb-8">
                  <div className="flex items-center gap-4">
                    <div className="font-semibold w-fit">
                      KEDA Configuration
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.keda`} />
                      </Only>
                    </div>
                    <Dropdown.Button
                      type={"primary"}
                      size={"small"}
                      onClick={() => {
                        let kedaObject: WorkloadAutoscalingKeda = {};
                        try {
                          kedaObject = jsYaml.load(keda || `{}`) as any;
                          if (typeof kedaObject !== "object" || kedaObject === null) {
                            throw new Error("KEDA configuration must be a YAML object, not a primitive value");
                          }
                        } catch (e: any) {
                          const errorMessage = e.message || "Invalid YAML syntax";
                          notification.error({
                            message: "Cannot Add Trigger",
                            description: `Current KEDA configuration has invalid YAML: ${errorMessage}`,
                          });
                          formSetValue(id, `defaultOptions.meta.kedaRaw`, keda);
                          return;
                        }
                        // Clear kedaRaw on successful parse
                        formSetValue(id, `defaultOptions.meta.kedaRaw`, "");
                        if (!kedaObject.triggers) {
                          kedaObject.triggers = [];
                        }
                        kedaObject.triggers.push({ type: "", metadata: {} });
                        const kedaString = jsYaml.dump(kedaObject);
                        formSetValue(id, `defaultOptions.autoscaling.keda`, kedaString);
                      }}
                      menu={{
                        style: { maxHeight: 300, overflowY: "scroll" },
                        items: kedaScalerOptions,
                        onClick: (e) => {
                          const scaler = allKedaScalers.find((s) => s.type === e.key);
                          if (!scaler) {
                            return;
                          }

                          let kedaObject: WorkloadAutoscalingKeda = {};
                          try {
                            kedaObject = jsYaml.load(keda || `{}`) as any;
                            if (typeof kedaObject !== "object" || kedaObject === null) {
                              throw new Error("KEDA configuration must be a YAML object, not a primitive value");
                            }
                          } catch (e: any) {
                            const errorMessage = e.message || "Invalid YAML syntax";
                            notification.error({
                              message: "Cannot Add Trigger",
                              description: `Current KEDA configuration has invalid YAML: ${errorMessage}`,
                            });
                            formSetValue(id, `defaultOptions.meta.kedaRaw`, keda);
                            return;
                          }
                          // Clear kedaRaw on successful parse
                          formSetValue(id, `defaultOptions.meta.kedaRaw`, "");
                          if (!kedaObject.triggers) {
                            kedaObject.triggers = [];
                          }

                          const trigger: KedaTrigger = {
                            type: scaler.type,
                            name: scaler.name,
                            metadata: scaler.metadata || {},
                          };
                          if (scaler.metricType) {
                            trigger.metricType = scaler.metricType as any;
                          }
                          kedaObject.triggers.push(trigger);
                          const kedaString = jsYaml.dump(kedaObject);
                          formSetValue(id, `defaultOptions.autoscaling.keda`, kedaString);
                        },
                      }}
                    >
                      Add Trigger
                    </Dropdown.Button>
                  </div>
                  <div className="flex flex-col gap-2">
                    <CpDescription>
                      <div className="flex items-center gap-1 mt-2 text-sm">
                        Learn more about KEDA{" "}
                        <ExternalLink
                          url={`${DOCS_URL}/reference/workload/autoscaling#keda`}
                          label="in our docs"
                          disableCopy
                        />
                      </div>
                    </CpDescription>
                    <CpDescription>
                      <div className="flex items-center gap-1 mb-4 text-sm">
                        See{" "}
                        <ExternalLink
                          url={`${`https://keda.sh/docs/2.17/scalers/`}`}
                          label="available keda scalers"
                          disableCopy
                        />
                      </div>
                    </CpDescription>
                  </div>
                  <CodeEditor
                    key={`defaultOptions`}
                    value={keda}
                    setValue={(value) => formSetValue(id, `defaultOptions.autoscaling.keda`, value)}
                    onChange={() => {
                      const name = "defaultOptions.autoscaling.keda";
                      formSet(id, `fieldStates.${name}.modified`, true);

                      const form = store.getState().form.instances[id];
                      const onChangeCounter = form.meta.onChangeCounter;
                      formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                    }}
                    onFocus={() => {
                      const name = "defaultOptions.autoscaling.keda";
                      formSet(id, `fieldStates.${name}.focused`, true);
                      formSet(id, `fieldStates.${name}.visited`, true);
                    }}
                    onBlur={() => {
                      const name = "defaultOptions.autoscaling.keda";
                      formSet(id, `fieldStates.${name}.focused`, false);
                      formSet(id, `fieldStates.${name}.touched`, true);

                      // Set kedaRaw on blur if YAML is invalid
                      try {
                        const parsed = jsYaml.load(keda || "{}");
                        if (typeof parsed !== "object" || parsed === null) {
                          throw new Error("Must be an object");
                        }
                        // Clear kedaRaw if valid
                        formSetValue(id, `defaultOptions.meta.kedaRaw`, "");
                      } catch (e: any) {
                        formSetValue(id, `defaultOptions.meta.kedaRaw`, keda);
                      }

                      const form = store.getState().form.instances[id];
                      const onBlurCounter = form.meta.onBlurCounter;
                      formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                    }}
                    language="yaml"
                    options={{ style: { fontSize: 14 } }}
                  />
                  <CpErrorWarning name={`defaultOptions.autoscaling.keda`} />
                </div>
              ) : null}
              {metric === "keda" ? <Divider /> : null}
              <CpFormItem>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col">
                    <CpLabel name={`defaultOptions.autoscaling.minScale`}>
                      Min Scale
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.minScale`} />
                      </Only>
                    </CpLabel>
                    <CpInputNumber name={`defaultOptions.autoscaling.minScale`} />
                  </div>
                  {["disabled"].includes(metric) ? null : (
                    <div className="flex flex-col">
                      <CpLabel name={`defaultOptions.autoscaling.maxScale`}>
                        Max Scale
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.maxScale`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`defaultOptions.autoscaling.maxScale`} />
                    </div>
                  )}
                  {type === "serverless" ? (
                    <div className="flex flex-col">
                      <CpLabel name={`defaultOptions.autoscaling.scaleToZeroDelay`}>
                        Scale Down Delay
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.scaleToZeroDelay`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`defaultOptions.autoscaling.scaleToZeroDelay`} addonAfter={`Seconds`} />
                    </div>
                  ) : null}
                  {type === "serverless" ? (
                    <div className="flex flex-col">
                      <CpLabel name={`defaultOptions.autoscaling.maxConcurrency`}>
                        Max Concurrency
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`defaultOptions.autoscaling.maxConcurrency`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`defaultOptions.autoscaling.maxConcurrency`} />
                    </div>
                  ) : null}
                </div>
                <CpErrorWarningArray
                  names={[
                    `defaultOptions.autoscaling.minScale`,
                    `defaultOptions.autoscaling.maxScale`,
                    `defaultOptions.autoscaling.scaleToZeroDelay`,
                    `defaultOptions.autoscaling.maxConcurrency`,
                  ]}
                />
                {type === "serverless" && minScale === "0" && !scaleToZeroSuggestionApplied ? (
                  <Alert
                    type="info"
                    showIcon
                    className="mt-4 w-fit"
                    style={{ paddingTop: 16, paddingBottom: 16 }}
                    message="Scale to Zero Configuration"
                    description={
                      <div className="flex flex-col gap-2">
                        <span>
                          For scale-to-zero workloads, we recommend the following configuration to allow spin-up of a
                          replica when the first request is received:
                        </span>
                        <ul className="list-disc pl-4 my-1">
                          <li>
                            <strong>Scale Down Delay:</strong> 30 seconds
                          </li>
                          <li>
                            <strong>Timeout Seconds:</strong> 120 seconds
                          </li>
                          <li>
                            <strong>Metric:</strong> Concurrent Requests
                          </li>
                        </ul>
                        <div className="mt-1">
                          <Button type="primary" onClick={applyScaleToZeroSuggestion}>
                            Apply Suggested Configuration
                          </Button>
                        </div>
                      </div>
                    }
                  />
                ) : null}
              </CpFormItem>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
});
